// Bulk delete utilities for Firestore
import { 
  collection, 
  getDocs, 
  doc, 
  writeBatch,
  query,
  limit
} from 'firebase/firestore';
import { db } from './config';

// Delete all documents in a collection (batch delete for efficiency)
export const deleteCollection = async (collectionName, batchSize = 500) => {
  try {
    console.log(`Starting bulk delete of collection: ${collectionName}`);
    
    const collectionRef = collection(db, collectionName);
    let deletedCount = 0;
    let hasMore = true;

    while (hasMore) {
      // Get a batch of documents
      const q = query(collectionRef, limit(batchSize));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        hasMore = false;
        break;
      }

      // Create a batch for efficient deletion
      const batch = writeBatch(db);
      
      querySnapshot.docs.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref);
      });

      // Commit the batch
      await batch.commit();
      
      deletedCount += querySnapshot.docs.length;
      console.log(`Deleted ${querySnapshot.docs.length} documents. Total: ${deletedCount}`);
      
      // If we got fewer documents than the batch size, we're done
      if (querySnapshot.docs.length < batchSize) {
        hasMore = false;
      }
    }

    console.log(`Bulk delete completed. Total documents deleted: ${deletedCount}`);
    return deletedCount;
  } catch (error) {
    console.error('Error in bulk delete:', error);
    throw error;
  }
};

// Delete specific documents by IDs
export const deleteDocumentsByIds = async (collectionName, documentIds) => {
  try {
    console.log(`Deleting ${documentIds.length} documents from collection: ${collectionName}`);
    
    const batch = writeBatch(db);
    
    documentIds.forEach((id) => {
      const docRef = doc(db, collectionName, id);
      batch.delete(docRef);
    });

    await batch.commit();
    console.log(`Successfully deleted ${documentIds.length} documents`);
    
    return documentIds.length;
  } catch (error) {
    console.error('Error deleting documents by IDs:', error);
    throw error;
  }
};

// Delete documents matching a condition
export const deleteDocumentsByCondition = async (collectionName, condition, batchSize = 500) => {
  try {
    console.log(`Deleting documents from ${collectionName} matching condition`);
    
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...condition);
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('No documents found matching the condition');
      return 0;
    }

    const batch = writeBatch(db);
    let deletedCount = 0;
    
    querySnapshot.docs.forEach((docSnapshot) => {
      batch.delete(docSnapshot.ref);
      deletedCount++;
    });

    await batch.commit();
    console.log(`Successfully deleted ${deletedCount} documents`);
    
    return deletedCount;
  } catch (error) {
    console.error('Error deleting documents by condition:', error);
    throw error;
  }
};

// Get collection statistics
export const getCollectionStats = async (collectionName) => {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    
    return {
      totalDocuments: querySnapshot.size,
      collectionName: collectionName
    };
  } catch (error) {
    console.error('Error getting collection stats:', error);
    throw error;
  }
}; 